/**
 * MachineScheduleBookingLane.tsx
 * The normal (non-Index) machine row's day-span content: background day
 * cells, baseline ghost bars (Compare-to-Baseline, 6B.8), real booking bars,
 * the active drag-ghost preview, the create-drag selection rectangle, and the
 * non-work (weekend/holiday) overlay.
 *
 * Extracted from MachineScheduleGrid.tsx (600-line cap, CODING-STANDARDS) —
 * Item 6.1 added a second row kind (the Index machine's daily % heatmap,
 * `MachineScheduleIndexRow`), and this extraction keeps the "one row = one
 * booking-bar lane" logic isolated from the row-kind branch in the grid.
 * Pure passthrough of the grid's existing render logic — no behavior change.
 */

import React from 'react';
import { Box } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { MachineBookingCalendar } from '../../../types/schedule';
import { BookingBar } from '../../../components/schedule/BookingBar';
import type { ConflictType } from '../../../components/schedule/ConflictBadge';
import { DragGhost } from '../../../components/schedule/DragGhost';
import type { ActiveDrag, DragMode } from '../../../hooks/useBookingDrag';
import type { CreateSelection } from '../../../hooks/useCreateDrag';
import { MachineScheduleDayCells } from './MachineScheduleDayCells';
import { MachineScheduleNonWorkOverlay } from './MachineScheduleNonWorkOverlay';
import { computeRangePosition, computeBarPosition } from './scheduleGridShared';
import { continuousDaySet } from '../../../utils/runModeRendering';

export interface MachineScheduleBookingLaneProps {
  machineKey: string;
  machineBookings: MachineBookingCalendar[];
  ghostBookings?: MachineBookingCalendar[];
  days: string[];
  hoverCol: number | null;
  dayColWidth: number;
  holidayNameByDate: Map<string, string>;
  dayCellColors: {
    borderColor: string;
    todayBorder: string;
    colHighlight: string;
    todayBg: string;
    holidayBg: string;
    weekendBg: string;
  };
  nonWorkColors: { holiday: string; weekend: string; continuousRun: string };
  startDate: string;
  endDate: string;
  totalDays: number;
  dayWidthPx: number;
  conflictTypeMap: Map<string, ConflictType>;
  selectedKey?: string | null;
  isBookingReadOnly?: (booking: MachineBookingCalendar) => boolean;
  activeDrag: ActiveDrag | null;
  showGhost: boolean;
  onBeginDrag: (booking: MachineBookingCalendar, mode: DragMode, e: React.PointerEvent) => void;
  createSel: CreateSelection | null;
}

export const MachineScheduleBookingLane: React.FC<MachineScheduleBookingLaneProps> = ({
  machineKey,
  machineBookings,
  ghostBookings,
  days,
  hoverCol,
  dayColWidth,
  holidayNameByDate,
  dayCellColors,
  nonWorkColors,
  startDate,
  endDate,
  totalDays,
  dayWidthPx,
  conflictTypeMap,
  selectedKey,
  isBookingReadOnly,
  activeDrag,
  showGhost,
  onBeginDrag,
  createSel,
}) => {
  const theme = useTheme();

  return (
    <>
      <MachineScheduleDayCells
        days={days}
        hoverCol={hoverCol}
        dayColWidth={dayColWidth}
        holidayNameByDate={holidayNameByDate}
        colors={dayCellColors}
      />

      {/* Ghost (baseline) bars — BEHIND scenario bars. 6B.8. */}
      {ghostBookings?.map((ghost) => {
        const pos = computeBarPosition(ghost, startDate, endDate, totalDays);
        if (!pos) return null;
        const ghostColor = ghost.EventType === 'OutOfService'
          ? theme.schedule.noActivity
          : ghost.EventType === 'Maintenance' || ghost.EventType === 'Calibration'
            ? theme.schedule.maintenance
            : ghost.Source === 'WorkOrder'
              ? theme.schedule.committed
              : theme.schedule.tentative;
        return (
          <Box
            key={`ghost-${ghost.MachineBookingKey}`}
            title={ghost.TaskName ?? ghost.EventType ?? ghost.Source ?? 'Baseline'}
            sx={{
              position: 'absolute', top: '4px', bottom: '4px',
              left: `${pos.leftFraction * 100}%`,
              width: `${pos.widthFraction * 100}%`,
              borderRadius: '3px',
              border: `2px solid ${alpha(ghostColor, 0.75)}`,
              backgroundColor: alpha(ghostColor, 0.18),
              opacity: 0.9, pointerEvents: 'none', zIndex: 0,
            }}
          />
        );
      })}

      {machineBookings.map((booking) => {
        const pos = computeBarPosition(booking, startDate, endDate, totalDays);
        if (!pos) return null;
        return (
          <BookingBar
            key={booking.MachineBookingKey}
            booking={booking}
            leftFraction={pos.leftFraction}
            widthFraction={pos.widthFraction}
            pxWidth={pos.widthFraction * totalDays * dayWidthPx}
            conflictType={conflictTypeMap.get(booking.MachineBookingKey) ?? null}
            readOnly={isBookingReadOnly?.(booking) ?? false}
            isActiveDrag={activeDrag?.bookingKey === booking.MachineBookingKey}
            selected={booking.MachineBookingKey === selectedKey}
            onBeginDrag={onBeginDrag}
          />
        );
      })}

      {/* Drag ghost — preview in the target row */}
      {showGhost && activeDrag && (() => {
        const gpos = computeRangePosition(
          activeDrag.ghostStart,
          activeDrag.ghostEnd,
          startDate,
          endDate,
          totalDays,
        );
        return gpos ? (
          <DragGhost
            leftFraction={gpos.leftFraction}
            widthFraction={gpos.widthFraction}
            valid={activeDrag.valid}
          />
        ) : null;
      })()}

      {/* Create-drag selection rectangle */}
      {createSel && createSel.machineKey === machineKey && (() => {
        const sp = computeRangePosition(
          days[createSel.loIdx],
          days[createSel.hiIdx],
          startDate,
          endDate,
          totalDays,
        );
        return sp ? (
          <Box
            sx={{
              position: 'absolute',
              top: '4px',
              bottom: '4px',
              left: `${sp.leftFraction * 100}%`,
              width: `${sp.widthFraction * 100}%`,
              border: `2px dashed ${theme.schedule.selection}`,
              backgroundColor: alpha(theme.schedule.selection, 0.12),
              borderRadius: '3px',
              pointerEvents: 'none',
              zIndex: 5,
            }}
          />
        ) : null;
      })()}

      {/* Non-work overlay (weekends + stat holidays) — drawn IN FRONT of the
          bars so a multi-day booking is visibly notched on the closed days it
          already carves out. pointer-events:none. Continuous (24/7) bookings'
          covered days switch to the "running" tint instead of the closed veil. */}
      <MachineScheduleNonWorkOverlay
        days={days}
        holidayNameByDate={holidayNameByDate}
        colors={nonWorkColors}
        continuousDays={continuousDaySet(machineBookings)}
      />
    </>
  );
};
